<script lang="ts">
  import {
    line,
    scaleLinear,
    extent,
    axisRight,
    axisBottom,
    scaleUtc,
    select,
    // timeFormatLocale
  } from "d3";
  import { type DaysData, type DayData } from "./daysData";
  import type { Omraade as Area } from "$lib/types/omraade";

  // const d3Locale = timeFormatLocale({
  //   dateTime: "%x, %X",
  //   date: "%-m/%-d/%Y",
  //   time: "%-I:%M:%S %p",
  //   periods: ["AM", "PM"],
  //   days: ["Søndag", "Mandag", "Tirsdag", "Onsdag", "Torsdag", "Fredag", "Lørdag"],
  //   shortDays: ["Søn", "Man", "Tir", "Ons", "Tor", "Fre", "Lør"],
  //   months: ["Januar", "Februar", "Mars", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Desember"],
  //   shortMonths: ["Jan", "Feb", "Mar", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Des"]
  // });

  const getPriceToLine = (prices: (undefined | number)[], px: number, notZeroMin = false) => {
    const setChartDomain = (
      extentDomain: [number | undefined | string, number | undefined | string]
    ) => {
      const [minimum, maximum] = extentDomain;
      const min = minimum ? +minimum : 0;
      const max = maximum ? +maximum : 0;
      const domainStart = notZeroMin ? min : Math.min(0, min);
      const domainEnd = maxY ? maxY : max;
      return [domainEnd, domainStart];
    };

    return !prices
      ? null
      : scaleLinear()
          .domain(
            setChartDomain(extent(prices.map((x) => x && +x).filter((x) => typeof x === "number")))
          ) // unit: øre/kWh
          .range([0, px]); // unit: pixels
  };

  const setItemIndex = (x: number | string) => {
    if (typeof x === "string" && days) {
      // check if x is date
      const day = days.find((day) => day[0] === x);

      if (day) {
        itemIndex = days.indexOf(day);
      }
    }

    if (typeof itemIndex === "number" && days) {
      selectedDayData = days[itemIndex];
    }
  };

  let {
    daysData,
    selectedAreas,
    maxY,
    children,
    selectedDay = $bindable(),
  }: {
    daysData?: DaysData;
    selectedAreas: Area[];
    maxY?: number;
    children?: import("svelte").Snippet;
    selectedDay?: string;
  } = $props();

  const strokeWidth = 2;
  const chartMinH = 150;
  let chartHeight = $state(chartMinH);
  let chartWidth = $state(0);
  // let motionX: number | undefined = $state();
  let itemIndex: number | undefined = $state();
  let hoverItemIndex: number | undefined = $state();
  let selectedDayData: undefined | [string, DayData] = $state();
  let days = $derived.by(() => {
    if (!daysData) return [];
    else return Array.from(daysData).reverse();
  });
  let daysAreas = $derived(days.map((day) => day[1]));
  let allPricesFlat = $derived(daysAreas.map((dayAreas) => Array.from(dayAreas.values())).flat());
  let priceToLine = $derived(getPriceToLine(allPricesFlat, chartHeight));
  let chartXOffset = $derived.by(() => {
    let itemInitW = chartWidth / days.length;
    return itemInitW / 2;
  });
  let scaleX = $derived(
    days?.length &&
      scaleUtc(
        [new Date(days[0][0]), new Date(days[days.length - 1][0])],
        [0, chartWidth - chartXOffset * 2]
      )
  );
  let dLine = $derived(
    days?.length &&
      scaleX &&
      priceToLine &&
      line(
        // x value
        (_: number, i: number) => scaleX(new Date(days[i][0])),
        // y value
        priceToLine
      )
  );

  // y axis
  let gy: SVGElement;
  $effect(() => {
    if (gy && priceToLine && priceToLine) {
      const el = select(gy);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (el) el.call(axisRight(priceToLine) as any);
    }
  });
  // x axis
  let gx: SVGElement;
  $effect(() => {
    if (gx && scaleX) {
      const el = select(gx);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (el) el.call(axisBottom(scaleX).tickArguments([5, "%_d/%m"]) as any);
    }
  });

  if (selectedDay?.length) {
    setItemIndex(selectedDay);
  }

  const chartInteraction = (chart: HTMLDivElement) => {
    let hasTouch = $state("ontouchstart" in document.documentElement);
    let movement = $derived.by(() => (hasTouch ? "touchmove" : "mousemove"));

    const movementAction = (
      event: MouseEvent | TouchEvent,
      opt: { hoverOnly?: boolean; toggleable?: boolean } = {}
    ) => {
      let x: number;

      if (hasTouch) {
        const e = event as TouchEvent;
        x = e.touches[0].clientX;
        hoverItemIndex = Math.floor((x / chartWidth) * days.length - 1);
      } else {
        const e = event as MouseEvent;
        const rect = chart.getBoundingClientRect();
        x = e.clientX - rect.left;
        hoverItemIndex = Math.floor((x / chartWidth) * days.length);
      }

      // motionX = x;

      if (opt?.hoverOnly) {
        // selectedDayData = days[itemIndex];
        // selectedDay = selectedDayData[0];
      } else {
        if (hoverItemIndex < 0) itemIndex = 0;
        else if (hoverItemIndex >= days.length) itemIndex = days.length - 1;
        else itemIndex = hoverItemIndex;

        selectedDayData = days[itemIndex];
        selectedDay = selectedDayData[0];
      }
    };

    $effect(() => {
      hasTouch = "ontouchstart" in document.documentElement;

      if (hasTouch) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        chart.addEventListener(movement, (event: any) => {
          movementAction(event);
        });
        chart.addEventListener("touchstart", (event) => {
          movementAction(event, { toggleable: true });
        });
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        chart.addEventListener(movement, (event: any) => {
          movementAction(event, { hoverOnly: true });
        });
      }

      // Always activate click incase both touch and mouse is used
      chart.addEventListener("click", function (event) {
        movementAction(event, { toggleable: true });
      });

      return () => {
        chart.removeEventListener(movement, () => {});
        chart.removeEventListener("touchstart", () => {});
        chart.removeEventListener("click", () => {});
      };
    });
  };
</script>

<div
  class="[--tick-y-left:calc(.5*var(--space-edge-x))] desktop:[--tick-y-left:var(--s-4)] component-container mx-.25edge-x px-1 px-.75edge-x desktop:mx-edge-x desktop:px-8 bg-white rounded-1rem shadow-[0_1px_3px_rgba(0,0,0,0.375)] isolate mt-3 text-sm pb-4"
>
  {@render children?.()}

  <div class="leading-none h-6 text-sm relative">
    {#if selectedDayData}
      <p class="m-0 text-center absolute w-full left-0 top-2 text-sm">
        <strong>
          {new Date(selectedDayData[0]).toLocaleDateString("nb-NO", {
            day: "numeric",
            month: "short",
            year: "numeric",
          })}
        </strong>
      </p>
    {/if}
    <p class="m-0 absolute right-0 top-2 text-sm">
      <strong> øre/kWh </strong>
    </p>
  </div>

  <div
    bind:clientWidth={chartWidth}
    bind:clientHeight={chartHeight}
    class="chart-container relative mb-4 isolate mr-2rem"
    style="height: clamp({chartMinH}px, 15vmin, {chartMinH *
      1.75}px); --h-chart: {chartHeight}px; --x-ticks: {days.length};"
  >
    <svg
      aria-hidden="true"
      width={chartWidth}
      height={chartHeight}
      viewBox="0 0 {chartWidth} {chartHeight}"
      class="absolute inset-0"
    >
      {#if typeof hoverItemIndex === "number"}
        <line
          class="hover-marker"
          x1={0}
          y1="0"
          x2={0}
          y2={chartHeight}
          stroke="var(--isbre)"
          stroke-width="1"
          style="transition: transform .075s var(--easing); transform:translateX({(chartWidth /
            days.length) *
            hoverItemIndex +
            chartXOffset}px)"
        />
      {/if}
      {#if typeof itemIndex === "number"}
        <line
          x1={0}
          y1="0"
          x2={0}
          y2={chartHeight}
          stroke="var(--glod)"
          stroke-width="1"
          style="transition: transform .075s var(--easing); transform:translateX({(chartWidth /
            days.length) *
            itemIndex +
            chartXOffset}px)"
        />
      {/if}
    </svg>

    <div
      aria-hidden="true"
      class="absolute inset-[0_0_-2em] z-1 hover:touch-none select-none"
      use:chartInteraction
    ></div>

    <svg
      aria-hidden="true"
      width={chartWidth}
      height={chartHeight}
      viewBox="0 0 {chartWidth} {chartHeight}"
      class="relative overflow-visible text-.375rem"
      style="left: {chartXOffset}px"
    >
      {#each selectedAreas as area (area.value)}
        {@const prices = daysAreas.map((x) => x.get(area.value) || 0)}
        {#if dLine && prices?.length}
          <path
            fill="none"
            stroke="var(--{area.value.toLowerCase()}-line-color,currentcolor)"
            stroke-width={strokeWidth}
            d={dLine(prices)}
          />
        {/if}
      {/each}
      <g bind:this={gx} class="translate-y-[calc(var(--h-chart)-1px)]"></g>
      <g bind:this={gy} style="transform:translateX({chartWidth + 3}px)"></g>
    </svg>
  </div>
</div>

<style lang="postcss">
  .chart-container {
    & .hover-marker {
      opacity: 0;
      transition: opacity 0.075s var(--easing);
    }

    &:hover .hover-marker {
      opacity: 1;
    }
  }
</style>
